<template>
  <Head :title="t('page.title')"></Head>
  <div class="bg-gradient-to-b from-blue-50 via-transparent to-transparent">
    <div class="p-4 lg:p-8 max-w-xl mx-auto flex flex-col gap-5">
      <form
        @submit.prevent="form.post(route('invitation.store'))"
        class="flex flex-col shadow-card rounded-lg"
      >
        <div
          class="bg-gradient-to-t from-white/70 to-white/30 flex items-center justify-center p-6 rounded-t-lg border border-white/50 border-b-gray-100"
        >
          <s-logo class="logo h-16 lg:h-[4.5rem] w-auto" />
        </div>
        <div
          class="px-5 lg:px-8 py-5 lg:py-8 bg-gradient-to-t from-gray-50 to-white rounded-b-lg flex flex-col gap-5"
        >
          <div class="flex flex-col gap-3">
            <p>
              {{ t('page.description') }}
            </p>
            <p>
              {{ t('page.notReceivedLink') }}
            </p>
          </div>
          <div class="group">
            <label for="email" class="sr-only">{{ t('email') }}</label>
            <s-input-field
              id="email"
              :placeholder="t('email')"
              v-model="form.email"
              required
              :error="form.errors.email"
            >
              <template v-slot:prefix>
                <mdicon
                  name="email"
                  class="opacity-70 group-hover:opacity-50 group-focus-within:text-blue-600 transition"
                />
              </template>
            </s-input-field>
          </div>
          <div class="mt-1 flex flex-wrap gap-4">
            <s-btn :href="route('login')" color="secondary" class="flex-1">
              {{ t('actions.backToLogin') }}
              <mdicon name="keyboard-return" class="opacity-70" role="presentation" />
            </s-btn>
            <s-btn
              type="submit"
              color="primary"
              :loading="form.processing"
              :disabled="form.processing"
              class="flex-1"
            >
              {{ t('actions.resend') }}
              <mdicon
                v-if="!form.processing"
                name="email-fast"
                class="opacity-70"
                role="presentation"
              />
              <mdicon v-else name="loading" class="opacity-70 animate-spin" role="presentation" />
            </s-btn>
          </div>
        </div>
      </form>
      <div class="flex gap-x-3 gap-y-2 flex-wrap">
        <s-link :href="route('support')" class="link-subtle flex items-center gap-1.5">
          <mdicon name="chat-question" class="opacity-70" />
          {{ t('help') }}
        </s-link>
      </div>
      <div
        v-if="flash['success']"
        class="px-4 py-2 shadow-sm border text-basse rounded flex items-center gap-2 bg-green-100 text-green-800 border-green-200"
      >
        <mdicon name="check-decagram" class="opacity-70" width="18" height="18" />
        {{ flash['success'] }}
      </div>
      <div
        v-else-if="flash['error']"
        class="px-4 py-2 shadow-sm border text-basse rounded flex items-center gap-2 bg-red-100 text-red-800 border-red-200"
      >
        <mdicon name="alert-decagram" class="opacity-70" width="18" height="18" />
        {{ flash['error'] }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {Head, useForm} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import {route} from 'ziggy-js';
import SInputField from '../../design-system/SInputField.vue';
import SLink from '../../components/SLink.vue';
import SBtn from '../../design-system/SBtn.vue';
import SLogo from '../../design-system/SLogo.vue';
import UnauthorizedLayout from '../../layouts/UnauthorizedLayout.vue';
import loginTranslations from '../Login/login.json';

defineOptions({layout: UnauthorizedLayout});

defineProps<{
  flash: Record<string, string>;
}>();

type FormData = {email: string};

const form = useForm<FormData>({
  email: '',
});

const {t} = useI18n({
  inheritLocale: true,
  messages: loginTranslations,
});
</script>
<i18n>
{
  "en": {
    "page": {
      "title": "Resend Invitation",
      "description": "Stemble is using an invite-only system. You will be added to the platform and will receive an email containing a registration link to allow you to claim your account.",
      "notReceivedLink": "If you have not received a link or have lost it, you can enter your email below to request another."
    }
  },
  "fr": {
    "page": {
      "title": "Renvoyer l'invitation",
      "description": "Stemble utilise un système d'invitation uniquement. Vous serez ajouté à la plateforme et recevrez un e-mail contenant un lien d'inscription pour vous permettre de réclamer votre compte.",
      "notReceivedLink": "Si vous n'avez pas reçu de lien ou l'avez perdu, vous pouvez entrer votre e-mail ci-dessous pour en demander un autre."
    }
  }
}
</i18n>
